<template>
  <tr class="body-item-row">
    <td class="text-text1 dark:text-dmText1">{{ `FR${shipment.id}` }}</td>
    <td class="text-text2 dark:text-dmText2 whitespace-nowrap">{{ getFullYear(shipment.created_at) }}</td>
    <td>
      <ShipmentAddressCol :shipment="shipment" />
    </td>
    <td>
      <div class="flex">
        <div v-if="shipment.shipping_type === ShippingMethods.Sea" class="flex items-center">
          <i class="fa fa-ship text-black dark:text-white" />
          <span class="pl-3 text-text1 dark:text-dmText1">{{ t('my_orders.table.body.by_ship') }}</span>
        </div>
        <div v-else-if="shipment.shipping_type === ShippingMethods.Air" class="flex items-center">
          <i class="fa fa-plane-departure text-black dark:text-white" />
          <span class="pl-3 text-text1 dark:text-dmText1">{{ t('my_orders.table.body.by_plane') }}</span>
        </div>
      </div>
    </td>
    <td>
      <div class="flex flex-col">
        <span class="text-text1 dark:text-dmText1">{{ getTotalWeight(shipment) }}</span>
      </div>
    </td>
    <td>
      <div v-if="shipment.invoice.amount !== null" class="flex items-center gap-1">
        <span class="text-text1 dark:text-dmText1">{{ toPrice(shipment.invoice.amount) }}</span>
        <AllShipmentPriceTooltip :shipment="shipment" />
      </div>
      <div v-else-if="shipment.price > 0" class="flex items-center gap-1">
        <span class="text-text1 dark:text-dmText1">{{ toPrice(shipment.price) }}</span>
        <AllShipmentPriceTooltip :shipment="shipment" />
      </div>
      <span v-else class="text-text1 dark:text-dmText1">-</span>
    </td>
    <td class="text-text2 dark:text-dmText2">{{ toPrice(shipment.customs_value) }}</td>
    <td>
      <div v-if="shipment.tracking.length" class="flex flex-col gap-1">
        <span>{{ shipment.tracking[0].number }}</span>
        <span
          class="font-bold text-brand-primary cursor-pointer hover:underline text-sm"
          @click="showTrackingsModal(shipment)">
          {{ t('site_pro.table.show_more') }}
        </span>
      </div>
      <span v-else>-</span>
    </td>
    <td>
      <div class="flex">
        <AllShipmentStatus :status="shipment.status" :fr-id="shipment.id" />
      </div>
    </td>
    <td v-if="showOpenAllButton">
      <div
        v-if="shipment.packages.length"
        class="flex items-center justify-around w-10 h-10 bg-neutral7 dark:bg-dmGray3 rounded-full cursor-pointer"
        @click="toggleOpenRow(shipment)">
        <i
          class="fa-solid text-neutral3 dark:text-dmText3"
          :class="{
            'fa-chevron-up !text-brand-primary': openedPackages.includes(shipment),
            'fa-chevron-down': !openedPackages.includes(shipment),
          }" />
      </div>
      <div v-else class="w-10 h-10"></div>
    </td>
  </tr>
  <template v-if="shipment.fr_data && shipment.fr_data.packages?.length">
    <LazyAllShipmentsTableRowPackages
      v-for="(warehouseItem, itemIndex) in getAllWarehouseItems(shipment.fr_data)"
      :key="itemIndex"
      :item="warehouseItem"
      :shipment="shipment"
      :opened-packages="openedPackages"
      @show-gallery-modal="showGalleryModal" />
  </template>
</template>

<script lang="ts" setup>
import type { AllPackageInterface, AllPackagePackageInterface } from '~/types/shipzeePro/packagesTypes'
import { getFullYear } from '~/utils/date/parseDate'
import { ShippingMethods } from '~/enums/ShippingMethods'
import type {
  ForwardRequestDataInterface,
  ForwardRequestDataWarehouseItemInterface,
} from '~/types/shipzeePro/shipzeeProTypes'
import ShipmentAddressCol from '~/components/SitePro/Shipment/ShipmentAddressCol.vue'

const { t } = useLang()

const props = defineProps<{
  shipment: AllPackageInterface
  openedPackages: AllPackageInterface[]
  currentPageItems: AllPackageInterface[]
}>()

const showOpenAllButton = computed(() =>
  props.currentPageItems.some((shipmentPackage: AllPackageInterface) => shipmentPackage.packages.length)
)

function getTotalWeight(shipment: AllPackageInterface) {
  if (shipment.packages.length) {
    const weight = shipment.packages
      .reduce((total: number, shipmentPackage: AllPackagePackageInterface) => total + shipmentPackage.weight, 0)
      .toFixed(2)

    return `${weight} ${t('site_pro.lb')}`
  }

  return '-'
}

const getAllWarehouseItems = (frData: ForwardRequestDataInterface) =>
  frData.packages?.reduce((prev: ForwardRequestDataWarehouseItemInterface[], next) => {
    return [...prev]?.concat(next.warehouse_items)
  }, [])

const emit = defineEmits(['toggleOpenRow', 'showTrackingsModal', 'showGalleryModal'])

function showGalleryModal(shipmentPackage: ForwardRequestDataWarehouseItemInterface) {
  emit('showGalleryModal', shipmentPackage)
}

function showTrackingsModal(shipment: AllPackageInterface) {
  emit('showTrackingsModal', shipment)
}

function toggleOpenRow(shipment: AllPackageInterface) {
  emit('toggleOpenRow', shipment)
}
</script>

<style lang="postcss" scoped>
.body-item-row {
  @apply border-neutral6 dark:border-dmGray4 border-t;

  &:first-child {
    @apply border-t-0;
  }
}

.wl {
  .toggle {
    @apply bg-brand-primaryLight;
  }
}
</style>
